import React, { useState, useEffect, useCallback } from 'react';

const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwm5h-p_EJG01ckL9oYTl5Uu13d7pJtfEJDQu-o0a5z11HsycEKsyyo3CV_WpypNlE/exec';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getInitials = (name = '') => {
  if (!name) return '?';
  return String(name).trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
};

const avatarColors = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#EAF3DE', color: '#3B6D11' },
  { bg: '#EEEDFE', color: '#3C3489' },
  { bg: '#FAEEDA', color: '#854F0B' },
  { bg: '#E1F5EE', color: '#0F6E56' },
  { bg: '#FAECE7', color: '#993C1D' },
];

const getAvatarColor = (name = '') => {
  if (!name) return avatarColors[0];
  const charCode = String(name).charCodeAt(0);
  return avatarColors[charCode % avatarColors.length] || avatarColors[0];
};

const statusBadge = (isActive) => ({
  label: isActive ? 'Active' : 'Inactive',
  bg: isActive ? '#EAF3DE' : '#F1EFE8',
  color: isActive ? '#3B6D11' : '#5F5E5A',
});

const approvalBadge = (val) => {
  if (!val) return null;
  const map = {
    Approved: { bg: '#EAF3DE', color: '#3B6D11' },
    Rejected: { bg: '#FCEBEB', color: '#A32D2D' },
    Activated: { bg: '#E6F1FB', color: '#185FA5' },
    'On Hold': { bg: '#FAEEDA', color: '#854F0B' },
  };
  return map[val] || { bg: '#F1EFE8', color: '#5F5E5A' };
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const S = {
  page: {
    height: '100vh',
    overflowY: 'auto',
    background: '#f1f5f9',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '20px 16px 40px',
    boxSizing: 'border-box',
  },
  wrap: { maxWidth: 960, margin: '0 auto' },
  header: {
    background: '#fff',
    borderRadius: '12px 12px 0 0',
    padding: '16px 22px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    borderBottom: '3px solid #1e3a8a',
    flexWrap: 'wrap',
  },
  logo: {
    width: 46, height: 46, borderRadius: 10,
    background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 22, fontWeight: 900, flexShrink: 0,
  },
  body: {
    background: '#fff',
    borderRadius: '0 0 12px 12px',
    padding: '22px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
  },
  input: {
    padding: '8px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: '#fff', color: '#111', fontFamily: 'inherit',
  },
  select: {
    padding: '8px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: '#fff', color: '#111', fontFamily: 'inherit', cursor: 'pointer',
  },
  btn: (primary) => ({
    padding: '8px 16px',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    background: primary ? 'linear-gradient(135deg,#1e3a8a,#2563eb)' : '#f1f5f9',
    color: primary ? '#fff' : '#374151',
  }),
  statCard: {
    background: '#f8fafc',
    borderRadius: 10,
    padding: '14px 18px',
    textAlign: 'center',
    flex: 1,
    minWidth: 90,
  },
  empCard: {
    background: '#fff',
    border: '1.5px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  badge: (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700,
    background: bg, color,
  }),
  modalOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '30px 16px',
    overflowY: 'auto',
  },
  modalBox: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e5e7eb',
    width: '100%',
    maxWidth: 680,
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    marginBottom: 30,
  },
  sectionHead: {
    fontSize: 11, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    margin: '18px 0 8px',
  },
  infoRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', padding: '7px 0',
    borderBottom: '1px solid #f1f5f9', fontSize: 13, gap: 12,
  },
};

// ─── Toggle Switch ───────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange }) => (
  <label
    style={{ position: 'relative', display: 'inline-flex', width: 44, height: 24, flexShrink: 0 }}
    onClick={e => e.stopPropagation()}
  >
    <input type="checkbox" checked={checked} onChange={onChange}
      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
    <span style={{
      position: 'absolute', inset: 0, borderRadius: 24,
      background: checked ? '#639922' : '#D3D1C7',
      cursor: 'pointer', transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', height: 18, width: 18,
        left: 3, bottom: 3, background: '#fff', borderRadius: '50%',
        transition: 'transform 0.2s',
        transform: checked ? 'translateX(20px)' : 'translateX(0)',
      }} />
    </span>
  </label>
);

// ─── Info Row ────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => {
  if (!value || value === 'Not Uploaded') return null;
  return (
    <div style={S.infoRow}>
      <span style={{ color: '#6b7280', flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

// ─── Chips ───────────────────────────────────────────────────────────────────

const Chips = ({ value }) => {
  if (!value) return null;
  const items = String(value).split(',').map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
      {items.map((item, i) => (
        <span key={i} style={{
          background: '#f1f5f9', color: '#374151',
          borderRadius: 6, padding: '2px 8px', fontSize: 11,
        }}>{item}</span>
      ))}
    </div>
  );
};

// ─── Modal Detail ────────────────────────────────────────────────────────────

const ModalDetail = ({ emp, onClose, onToggleActive }) => {
  const initials = getInitials(emp.fullName);
  const av = getAvatarColor(emp.fullName);
  const isActive = emp._active;
  const sb = statusBadge(isActive);
  const ab = approvalBadge(emp.finalStatus);

  const sections = [
    {
      title: '1. Personal Information',
      rows: [
        ['Full Name', emp.fullName], ['CNIC / ID', emp.cnic],
        ['Date of Birth', emp.dob], ['Nationality', emp.nationality],
        ['Mobile', emp.mobile], ['Email', emp.email],
        ['Permanent Address', emp.permanentAddress],
        ['Current Address', emp.currentAddress],
        ['Emergency Contact', emp.emergencyName],
        ['Emergency Number', emp.emergencyContact],
        ['Relation', emp.relation], ['Blood Group', emp.bloodGroup],
      ],
    },
    {
      title: '2. Documents',
      rows: [
        ['Photo', emp.photoFile], ['CNIC Front', emp.cnicFrontFile],
        ['CNIC Back', emp.cnicBackFile],
      ],
    },
    {
      title: '3. Job Source & References',
      rows: [
        ['Job Source', emp.jobSource], ['Other Source', emp.jobSourceOther],
        ['Reference 1', emp.ref1Name ? `${emp.ref1Name} — ${emp.ref1Contact}` : ''],
        ['Reference 2', emp.ref2Name ? `${emp.ref2Name} — ${emp.ref2Contact}` : ''],
      ],
    },
    {
      title: '4. Hiring & Documentation',
      rows: [
        ['Position Applied', emp.positionApplied],
        ['Interview Date', emp.interviewDate],
        ['Interviewed By', emp.interviewedBy],
        ['Hiring Decision', emp.hiringDecision],
        ['Mandatory Docs', emp.mandatoryDocs],
        ['Background Check', emp.backgroundVerification],
        ['Right to Work', emp.rightToWork],
      ],
    },
    {
      title: '5. Employment Details',
      rows: [
        ['Employee ID', emp.empId], ['Date of Joining', emp.doj],
        ['Employment Type', emp.employmentType],
        ['Department', emp.department], ['Designation', emp.designation],
        ['Reporting Manager', emp.reportingManager],
        ['Salary Structure', emp.salaryStructure],
        ['Probation End', emp.probationEnd],
      ],
    },
    {
      title: '6. NOVAA System Access',
      rows: [
        ['Access Level', emp.accessLevel],
        ['NOVAA Account', emp.novaAccount],
        ['GPS Tracking', emp.gpsTracking],
        ['Mobile Device', emp.mobileDeviceAssigned],
        ['Device ID', emp.deviceId],
        ['Profile Status', emp.profileStatus],
        ['Activation Date', emp.activationDate],
      ],
    },
    {
      title: '7. Assets Issued',
      rows: [
        ['Uniform', emp.uniform], ['PPE', emp.ppe],
        ['Equipment', emp.equipment],
        ['Asset Serials', emp.assetSerials],
        ['Issued By', emp.assetIssuedBy],
        ['Digital Signature', emp.digitalSignature],
      ],
    },
    {
      title: 'Final Approval',
      rows: [
        ['HR Approval', emp.hrApproval],
        ['Ops Approval', emp.opsApproval],
        ['Final Status', emp.finalStatus],
      ],
    },
  ];

  const isMultiVal = (val) => val && String(val).includes(',');

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>

        {/* Modal Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: av.bg, color: av.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{emp.fullName || '—'}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{emp.designation || 'No Designation'} {emp.department ? `· ${emp.department}` : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {ab && <span style={S.badge(ab.bg, ab.color)}>{emp.finalStatus}</span>}
            <span style={S.badge(sb.bg, sb.color)}>{sb.label}</span>
            <Toggle checked={isActive} onChange={() => onToggleActive(emp)} />
            <button onClick={onClose} style={{ ...S.btn(false), padding: '6px 10px', fontSize: 16, borderRadius: '50%' }}>✕</button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '4px 24px 8px', maxHeight: '70vh', overflowY: 'auto' }}>
          {sections.map(sec => {
            const hasData = sec.rows.some(([, v]) => v && v !== 'Not Uploaded');
            if (!hasData) return null;
            return (
              <div key={sec.title}>
                <div style={S.sectionHead}>{sec.title}</div>
                {sec.rows.map(([label, val]) => {
                  if (!val || val === 'Not Uploaded') return null;
                  return (
                    <div key={label} style={S.infoRow}>
                      <span style={{ color: '#6b7280', flexShrink: 0, marginRight: 8 }}>{label}</span>
                      {isMultiVal(val)
                        ? <Chips value={val} />
                        : <span style={{ color: '#111827', fontWeight: 600, textAlign: 'right' }}>{val}</span>
                      }
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Employee Card ───────────────────────────────────────────────────────────

const EmpCard = ({ emp, onClick, onToggleActive }) => {
  const initials = getInitials(emp.fullName);
  const av = getAvatarColor(emp.fullName);
  const isActive = emp._active;
  const sb = statusBadge(isActive);
  const ab = approvalBadge(emp.finalStatus);

  return (
    <div style={S.empCard} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: av.bg, color: av.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.fullName || '—'}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.designation || 'No Designation'}</div>
        </div>
        {/* Active Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
          <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? '#3B6D11' : '#9ca3af' }}>{isActive ? 'Active' : 'Inactive'}</span>
          <Toggle checked={isActive} onChange={() => onToggleActive(emp)} />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <span style={S.badge(sb.bg, sb.color)}>● {sb.label}</span>
        {emp.department && <span style={S.badge('#f1f5f9', '#374151')}>{String(emp.department).split(',')[0].trim()}</span>}
        {ab && emp.finalStatus && <span style={S.badge(ab.bg, ab.color)}>{emp.finalStatus}</span>}
      </div>

      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
        {emp.mobile && <div style={{ color: '#6b7280' }}>📱 {emp.mobile}</div>}
        {emp.doj && <div style={{ color: '#6b7280' }}>📅 DOJ: {emp.doj}</div>}
        {emp.employmentType && <div style={{ color: '#6b7280' }}>💼 {emp.employmentType}</div>}
        {emp.hiringDecision && <div style={{ color: '#6b7280' }}>✅ {emp.hiringDecision}</div>}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
        Click to view full profile →
      </div>
    </div>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

const NovaEmployeeDashboard = () => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);

  // Fetch from Google Sheets via Apps Script
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(SCRIPT_URL + '?action=getAll');
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        // Add _active field — default true if not already set
        const data = json.data.map(emp => ({
          ...emp,
          _active: emp._active !== undefined ? emp._active : (emp.profileStatus === 'Activated' || emp.finalStatus === 'Activated'),
        }));
        setAllEmployees(data);
        setEmployees(data);
      } else {
        throw new Error(json.message || 'No data returned');
      }
    } catch (err) {
      setError('Could not fetch data. Make sure your Google Apps Script is deployed and has a getAll action. Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter
  useEffect(() => {
    let list = [...allEmployees];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.fullName || '').toString().toLowerCase().includes(q) ||
        (e.designation || '').toString().toLowerCase().includes(q) ||
        (e.department || '').toString().toLowerCase().includes(q) ||
        (e.cnic || '').toString().includes(q)
      );
    }
    if (filterStatus === 'active') list = list.filter(e => e._active);
    if (filterStatus === 'inactive') list = list.filter(e => !e._active);
    setEmployees(list);
  }, [search, filterStatus, allEmployees]);

  const handleToggleActive = async (emp) => {
    const updated = allEmployees.map(e =>
      e === emp || (e.cnic && e.cnic === emp.cnic)
        ? { ...e, _active: !e._active }
        : e
    );
    setAllEmployees(updated);
    if (selected && selected.cnic === emp.cnic) {
      setSelected(prev => ({ ...prev, _active: !prev._active }));
    }

    // Optionally push active status back to Google Sheets
    try {
      await fetch(SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ action: 'updateStatus', cnic: emp.cnic, active: !emp._active }),
      });
    } catch (_) { /* fail silently */ }
  };

  // Stats
  const total = allEmployees.length;
  const activeCount = allEmployees.filter(e => e._active).length;
  const inactiveCount = total - activeCount;
  const approvedCount = allEmployees.filter(e => e.finalStatus === 'Activated' || e.hrApproval === 'Approved').length;

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* Header */}
        <div style={S.header}>
          <div style={S.logo}>N</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>NOVAA Employee Dashboard</div>
            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 2 }}>SoWash Onboarding Records</div>
          </div>
        </div>

        {/* Body */}
        <div style={S.body}>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
            <input
              style={{ ...S.input, flex: 1, minWidth: 160 }}
              placeholder="Search by name, CNIC, designation..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select style={S.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Employees</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <button style={S.btn(true)} onClick={fetchData}>
              {loading ? '⏳ Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>{total}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Total Employees</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#3B6D11' }}>{activeCount}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Active</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#5F5E5A' }}>{inactiveCount}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Inactive</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#185FA5' }}>{approvedCount}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Approved / Activated</div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#A32D2D', marginBottom: 16 }}>
              ⚠️ {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6b7280' }}>
              ⏳ Fetching employee records from Google Sheets...
            </div>
          )}

          {/* Empty */}
          {!loading && !error && employees.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6b7280' }}>
              {allEmployees.length === 0
                ? 'No records found. Click Refresh to load data from Google Sheets.'
                : 'No employees match your search/filter.'}
            </div>
          )}

          {/* Grid */}
          {!loading && employees.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {employees.map((emp, i) => (
                <EmpCard
                  key={emp.cnic || i}
                  emp={emp}
                  onClick={() => setSelected(emp)}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <ModalDetail
          emp={selected}
          onClose={() => setSelected(null)}
          onToggleActive={handleToggleActive}
        />
      )}
    </div>
  );
};

export default NovaEmployeeDashboard;